/*
 * FEDERAÇÃO DOS PROFISSIONAIS DA BELEZA
 * FRONTEND - AUTH SLICE
 *
 * UPDATED: 2026-04-27 — v2.2
 * ✅ linkedAffiliateId — aluno promovido a filiado (acesso dual)
 * ✅ promotedAs        — 'professional' | 'affiliate' (via diploma)
 * ✅ generatedId       — PRF/FIL-YEAR-XXXX gerado na promoção
 * ✅ registroFepcssp   — número aleatório 6 dígitos da federação
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_DATA_KEY } from '../../services/api';

export interface AuthUser {
  id:        string;
  name:      string;
  email:     string;
  role:      'student' | 'affiliate' | 'admin';
  userType:  'student' | 'affiliate' | 'admin';
  photoUrl?: string;
  systemId?: string;

  // ── Filiado ───────────────────────────────────────────────
  affiliateId?: string;
  schoolName?:  string;
  logoUrl?:     string;

  // ── Aluno ─────────────────────────────────────────────────
  studentId?: string;
  status?:    'active' | 'pending' | 'inactive' | 'suspended' | 'graduated';

  // ── Promoção via diploma [NOVO] ───────────────────────────
  linkedAffiliateId?: string | null; // userId vinculado a um affiliate após promoção
  promotedAs?:        'professional' | 'affiliate' | null;
  generatedId?:       string | null; // PRF-2026-XXXX ou FIL-2026-XXXX
  registroFepcssp?:   string | null; // ex: 847362
}

interface AuthState {
  user:            AuthUser | null;
  token:           string | null;
  isAuthenticated: boolean;
  loading:         boolean;
  error:           string | null;
}

const initialState: AuthState = {
  user: null, token: null,
  isAuthenticated: false, loading: false, error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ user: AuthUser; token: string }>) => {
      state.user            = action.payload.user;
      state.token           = action.payload.token;
      state.isAuthenticated = true;
      state.loading         = false;
      state.error           = null;
      localStorage.setItem(TOKEN_KEY,     action.payload.token);
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(action.payload.user));
    },
    logout: (state) => {
      state.user            = null;
      state.token           = null;
      state.isAuthenticated = false;
      state.loading         = false;
      state.error           = null;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
    },
    restoreSession: (state) => {
      const token   = localStorage.getItem(TOKEN_KEY);
      const userStr = localStorage.getItem(USER_DATA_KEY);
      if (token && userStr) {
        try {
          state.token           = token;
          state.user            = JSON.parse(userStr) as AuthUser;
          state.isAuthenticated = true;
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_DATA_KEY);
        }
      }
    },
    setUser: (state, action: PayloadAction<AuthUser>) => {
      state.user            = action.payload;
      state.isAuthenticated = true;
      state.loading         = false;
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(action.payload));
    },
    updateUser: (state, action: PayloadAction<Partial<AuthUser>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(state.user));
      }
    },

    // [NOVO] Troca o portal ativo sem fazer logout
    // Usado quando aluno promovido clica em "Acessar Portal Filiado"
    switchToAffiliatePortal: (state) => {
      if (state.user?.linkedAffiliateId) {
        state.user = {
          ...state.user,
          userType:    'affiliate',
          role:        'affiliate',
          affiliateId: state.user.linkedAffiliateId,
        };
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(state.user));
      }
    },

    // [NOVO] Volta ao portal aluno (acesso dual reverso)
    switchToStudentPortal: (state) => {
      if (state.user) {
        state.user = {
          ...state.user,
          userType: 'student',
          role:     'student',
        };
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(state.user));
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error   = action.payload;
      state.loading = false;
    },
  },
});

export const {
  login, logout, restoreSession,
  setUser, updateUser, setLoading, setError,
  switchToAffiliatePortal, // [NOVO]
  switchToStudentPortal,   // [NOVO]
} = authSlice.actions;

export default authSlice.reducer;

// ── Selectors ─────────────────────────────────────────────────────────────────
type S = { auth: AuthState };

export const selectUser            = (s: S) => s.auth.user;
export const selectToken           = (s: S) => s.auth.token;
export const selectIsAuthenticated = (s: S) => s.auth.isAuthenticated;
export const selectAuthLoading     = (s: S) => s.auth.loading;
export const selectAuthError       = (s: S) => s.auth.error;

// Filiado
export const selectAffiliateId = (s: S) => s.auth.user?.affiliateId;
export const selectSchoolName  = (s: S) => s.auth.user?.schoolName;
export const selectLogoUrl     = (s: S) => s.auth.user?.logoUrl;

// Aluno
export const selectStudentId = (s: S) => s.auth.user?.studentId;

// Compartilhados
export const selectUserType   = (s: S) => s.auth.user?.userType;
export const selectSystemId   = (s: S) => s.auth.user?.systemId;
export const selectUserStatus = (s: S) => s.auth.user?.status;

// Promoção via diploma [NOVO]
export const selectPromotedAs        = (s: S) => s.auth.user?.promotedAs;
export const selectGeneratedId       = (s: S) => s.auth.user?.generatedId;
export const selectRegistroFepcssp   = (s: S) => s.auth.user?.registroFepcssp;
export const selectLinkedAffiliateId = (s: S) => s.auth.user?.linkedAffiliateId;
export const selectIsPromoted        = (s: S) => !!s.auth.user?.promotedAs;